/* eslint-disable react/forbid-prop-types,import/prefer-default-export,react/jsx-props-no-spreading,no-nested-ternary,import/no-unresolved */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { isMobile } from 'react-device-detect';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import CardIcon from 'assets/images/card.svg';
import { USER_PREFERENCES } from '../../../../../../../utilities/sharedQueries';
import { useUserCardProvider } from '../../../../../../../utilities/sharedHooks/user';
import { hiddenTransactionTypes } from '../../../../../../../screens/Accounts/constants';
import { TRANSACTION_TYPE } from '../../../../../../../screens/Accounts/enums';
import { getCardCapabilities } from '../../../../../../../screens/Card/constants';
import { formatNumber } from '../../../../../../../utilities/formatHelpers';
import { currencies, exchange } from '../../../../../../../utilities/constants';

const getTransactionDisplayValues = (tx, fiatCurrencyCode, wallet) => {
  const isOutgoing = tx.source_address === wallet?.address;
  const walletCoin = wallet?.coin.toUpperCase();
  const currencyCode = fiatCurrencyCode.toUpperCase();

  let hasTxRate;
  let txRate;
  if (walletCoin === currencyCode) {
    txRate = 1;
    hasTxRate = true;
  } else if (!tx.source_market_rate) {
    hasTxRate = false;
  } else if (tx.source_market_rate[walletCoin + currencyCode]) {
    txRate = tx.source_market_rate[walletCoin + currencyCode].ask;
    hasTxRate = true;
  } else if (tx.source_market_rate[currencyCode + walletCoin]) {
    txRate = tx.source_market_rate[currencyCode + walletCoin].ask;
    hasTxRate = true;
  }

  const { conversion } = currencies[wallet?.coin];
  const amount = isOutgoing ? tx.total_amount : tx.base_amount;

  return {
    amount,
    conversion,
    txRate,
    hasTxRate,
    isOutgoing,
  };
};

export const Transaction = ({ date, data, wallet, onTransactionSelected }) => {
  const { t: translate } = useTranslation();
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrencyCode = userPref?.displayCurrency || 'EUR';
  const { cardProvider } = useUserCardProvider();
  return (
    <div style={{ zIndex: 1 }} key={date} className="account-details-trans-item">
      <p className="text-center text-shadow text-f6 account-details-trans-item-group">{date}</p>
      <div className="">
        {data.map((tx) => {
          if (hiddenTransactionTypes.includes(tx.transaction_type)) {
            return <></>;
          }
          const { hasTxRate, txRate, amount, conversion } = getTransactionDisplayValues(
            tx,
            fiatCurrencyCode,
            wallet
          );

          const isAddFunds =
            tx.transaction_type === TRANSACTION_TYPE.RECEIVE ||
            tx.source_address !== wallet?.address;

          const isRefundOrCreditTransaction =
            tx.transaction_type === TRANSACTION_TYPE.RECEIVE && tx?.acceptor_name_location !== null;

          const isCashbackReward = tx.transaction_type === TRANSACTION_TYPE.CASHBACK_REWARD;
          const isAddFundsFromExternalAddress =
            tx.transaction_type === TRANSACTION_TYPE.SEND && tx.source_address !== wallet?.address;

          const isBufferSendTransaction =
            tx.transaction_type === TRANSACTION_TYPE.SEND &&
            tx.source_address === wallet?.address &&
            tx.destination_address?.toLowerCase() === 'buffer';

          const attribs = {};
          if (isMobile) {
            attribs.onClick = () => onTransactionSelected(tx);
            attribs.onKeyDown = () => onTransactionSelected(tx);
            attribs.tabIndex = '0';
            attribs.role = 'button';
          }

          return (
            <div
              {...attribs}
              className="flex-row-center gap-3 bg-lighter px-3 rounded-3 mt-3 mt-md-2 py-0 py-md-3"
              key={tx.id}>
              <div className="d-flex flex-row flex-fill justify-content-start align-items-start col-6">
                {isCashbackReward ? (
                  <img
                    src="./assets/images/cashback.svg"
                    alt="cashback"
                    className="me-3"
                    style={{ width: '2.667rem', height: '2.667rem' }}
                  />
                ) : isAddFunds ? (
                  <i className="icon-icome-navigation text-shadow text-f3 me-3 border rounded-4 p-1 border-disabled" />
                ) : (
                  <i className="icon-outcome-navigation text-shadow text-f3 me-3 border rounded-4 p-1 border-disabled" />
                )}
                <div>
                  <p className="text-start">
                    {isBufferSendTransaction || isRefundOrCreditTransaction ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <p className="d-none d-md-block text-start text-break">
                          {getCardCapabilities(cardProvider).truncatesMerchantLocation
                            ? tx.acceptor_name_location?.split(',')[0]
                            : tx.acceptor_name_location}
                        </p>
                        <p className="d-block d-md-none w-75 text-start text-break">
                          {getCardCapabilities(cardProvider).truncatesMerchantLocation
                            ? tx.acceptor_name_location?.split(',')[0]
                            : tx.acceptor_name_location}
                        </p>
                        <img src={CardIcon} alt="card" className="mx-2" />
                      </div>
                    ) : isAddFundsFromExternalAddress ? (
                      translate(`transaction_type_${TRANSACTION_TYPE.RECEIVE}_title`)
                    ) : (
                      translate(`transaction_type_${tx.transaction_type}_title`)
                    )}
                  </p>
                  <p className="text-shadow text-f6">{lodash.upperFirst(tx.transaction_state)}</p>
                </div>
              </div>
              <div className="upper-case">
                <p>
                  <b className={isAddFunds ? 'text-success' : ''}>
                    {formatNumber(
                      amount * conversion,
                      currencies[wallet?.coin?.toUpperCase()].decimal
                    )}{' '}
                    {wallet?.coin}
                  </b>
                </p>
                <p className="text-shadow text-f6 text-end text-md-start">
                  {hasTxRate
                    ? `${formatNumber(
                        amount * conversion * txRate,
                        exchange.FIAT_VALUE
                      )} ${fiatCurrencyCode}`
                    : 'N/A'}
                </p>
              </div>
              <div className="d-none d-md-flex flex-row justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn text-primary rounded-3 py-2 text-f6 text-btn-item"
                  onClick={() => onTransactionSelected(tx)}>
                  <b>{translate('transaction_button_label')}</b>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

Transaction.propTypes = {
  date: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  wallet: PropTypes.object.isRequired,
  onTransactionSelected: PropTypes.func.isRequired,
};
